/**
 * useCollectValues — POST /enterprise/connectors/{conn_id}/collect-values.
 *
 * Values-only sampling (DISTINCT values / ranges) WITHOUT LLM profiling: the
 * companion of a hand-curated schema. A connector whose descriptions were
 * imported from a vendor data dictionary (``user_edited``) must never go
 * through "Scopri" again — that would re-run the AI and pay for text we then
 * refuse to overwrite — but it still needs the code values the text-to-SQL
 * prompt relies on (``IST_STATO = 'E'`` …). This is that step, exposed so the
 * person who did the manual profiling is autonomous.
 *
 * The BE answers 202 and works in the background (seconds to a few minutes
 * on a few hundred tables); it writes ``schema_json`` only, so descriptions
 * and the business context are untouched. ``onSuccess`` invalidates the
 * schema and profiling queries so the viewer picks the values up on its
 * next refetch.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { type CollectValuesBody, collectEnterpriseValues } from "@ando/core/api";

import { enterpriseProfilingKey, enterpriseSchemaKey } from "./enterpriseKeys";

export interface UseCollectValuesVars {
  readonly connId: string;
  readonly body?: CollectValuesBody;
}

export function useCollectValues() {
  const qc = useQueryClient();
  return useMutation<void, Error, UseCollectValuesVars>({
    mutationFn: ({ connId, body }) => collectEnterpriseValues(connId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: enterpriseSchemaKey(vars.connId) });
      void qc.invalidateQueries({ queryKey: enterpriseProfilingKey(vars.connId) });
    },
  });
}
